import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Select,
    SelectChangeEvent,
    Snackbar,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { DataGridPremium, GridSortModel } from '@mui/x-data-grid-premium';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Organization } from 'utils';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import {
    GetOrgSubscriptionParams,
    getOrgSubscriptions,
    getUserSubscriptions,
    GetUserSubscriptionParams,
    getSubscriptionItemChangeRequests,
    GetSubscriptionItemChangeRequestParams,
    updateSubscriptionItemChangeRequest,
    getAthleteChangeRequests,
    GetAthleteChangeRequestParams,
    updateAthleteChangeRequest,
    getBillingPortalConfigurations,
    BillingPortalConfiguration,
} from '../subscriptions.api';
import { OrgSubscription, UserSubscription } from '../subscriptions.types';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import MenuItem from '@mui/material/MenuItem';
import {
    AthleteChangeRequest,
    Subscription,
    SubscriptionItemChangeRequest,
} from '../../../../../app.types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ResponsiveFormItem = styled(Box)(({ theme }) => ({
    width: 'calc(25% - 16px)',
    maxWidth: '300px',
    margin: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        width: 'calc(100% - 16px)',
        maxWidth: 'none',
    },
}));

const ResponsiveForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    margin: theme.spacing(-1),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const getCarrierTrackingUrl = (carrier: string | null, trackingNumber: string): string => {
    const normalizedCarrier = (carrier || '').toLowerCase();
    const encodedTracking = encodeURIComponent(trackingNumber.trim());

    if (normalizedCarrier.includes('ups')) {
        return `https://www.ups.com/track?loc=en_US&tracknum=${encodedTracking}`;
    }
    if (normalizedCarrier.includes('fedex')) {
        return `https://www.fedex.com/fedextrack/?trknbr=${encodedTracking}`;
    }
    if (normalizedCarrier.includes('usps')) {
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodedTracking}`;
    }
    if (normalizedCarrier.includes('dhl')) {
        return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encodedTracking}`;
    }

    return `https://www.google.com/search?q=${encodedTracking}+tracking`;
};

const renderTrackingNumberLink = (
    trackingNumber: string | null,
    carrier: string | null,
): React.ReactNode => {
    if (!trackingNumber) {
        return 'N/A';
    }

    return (
        <a
            href={getCarrierTrackingUrl(carrier, trackingNumber)}
            target="_blank"
            rel="noopener noreferrer"
        >
            {trackingNumber}
        </a>
    );
};

type SelectedGrid =
    | 'subscription_item_change_requests'
    | 'athlete_change_requests'
    | 'organization_subscriptions'
    | 'user_subscriptions'
    | 'billing_portal_configurations';

const SubscriptionList = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedGrid, setSelectedGrid] = useState<SelectedGrid>(
        'subscription_item_change_requests',
    );
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [orgSubscriptions, setOrgSubscriptions] = useState<OrgSubscription[]>([]);
    const [orgSearchParams, setOrgSearchParams] = useState<GetOrgSubscriptionParams>({
        name: '',
        organization_uuid: '',
        stripe_status: 'active',
        sort: '-updated_at',
    });
    const [orgPaginationModel, setOrgPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [orgTotalRows, setOrgTotalRows] = useState(0);
    const orgSearchParamsRef = useRef(orgSearchParams);

    const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
    const [userSearchParams, setUserSearchParams] = useState<GetUserSubscriptionParams>({
        name: '',
        user_name: '',
        stripe_status: 'active',
        sort: '-updated_at',
    });
    const [userPaginationModel, setUserPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [userTotalRows, setUserTotalRows] = useState(0);
    const userSearchParamsRef = useRef(userSearchParams);

    const [changeRequests, setChangeRequests] = useState<SubscriptionItemChangeRequest[]>([]);
    const [changeRequestSearchParams, setChangeRequestSearchParams] = useState({
        organization_uuid: '',
        status: '',
        sort: '-created_at',
    });
    const [changeRequestPaginationModel, setChangeRequestPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [changeRequestTotalRows, setChangeRequestTotalRows] = useState(0);
    const changeRequestSearchParamsRef = useRef(changeRequestSearchParams);

    const [athleteChangeRequests, setAthleteChangeRequests] = useState<AthleteChangeRequest[]>([]);
    const [athleteChangeRequestSearchParams, setAthleteChangeRequestSearchParams] = useState({
        status: '',
        sort: '-created_at',
    });
    const [athleteChangeRequestPaginationModel, setAthleteChangeRequestPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [athleteChangeRequestTotalRows, setAthleteChangeRequestTotalRows] = useState(0);
    const athleteChangeRequestSearchParamsRef = useRef(athleteChangeRequestSearchParams);
    const [athleteEditedRows, setAthleteEditedRows] = useState<{ [key: string]: any }>({});

    const [snackMessage, setSnackMessage] = useState<string>('');
    const [billingPortalConfigs, setBillingPortalConfigs] = useState<BillingPortalConfiguration[]>(
        [],
    );

    const dataGridSx = {
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
            {
                outline: 'none',
            },
    };

    useEffect(() => {
        getBillingPortalConfigurations()
            .then((response) => {
                setBillingPortalConfigs(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch billing portal configurations', error);
            });
    }, []);

    useEffect(() => {
        changeRequestSearchParamsRef.current = changeRequestSearchParams;
        orgSearchParamsRef.current = orgSearchParams;
        userSearchParamsRef.current = userSearchParams;
        athleteChangeRequestSearchParamsRef.current = athleteChangeRequestSearchParams;
    }, [
        changeRequestSearchParams,
        orgSearchParams,
        userSearchParams,
        athleteChangeRequestSearchParams,
    ]);

    useEffect(() => {
        // Fetch SubscriptionItemChangeRequests
        const params = {
            ...changeRequestSearchParamsRef.current,
            per_page: changeRequestPaginationModel.pageSize,
            page: changeRequestPaginationModel.page + 1,
        };
        getSubscriptionItemChangeRequests(params as GetSubscriptionItemChangeRequestParams)
            .then((response) => {
                setChangeRequests(response.data.data);
                setChangeRequestTotalRows(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch change requests', error);
            });
    }, [changeRequestPaginationModel]);

    useEffect(() => {
        const params = {
            ...orgSearchParamsRef.current,
            per_page: orgPaginationModel.pageSize,
            page: orgPaginationModel.page + 1,
        };
        getOrgSubscriptions(params)
            .then((response) => {
                setOrgSubscriptions(
                    response.data.data.map((item: OrgSubscription) => ({ ...item, id: item.id })),
                );
                setOrgTotalRows(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch subscriptions', error);
            });
    }, [orgPaginationModel]);

    useEffect(() => {
        const params = {
            ...userSearchParamsRef.current,
            per_page: userPaginationModel.pageSize,
            page: userPaginationModel.page + 1,
        };
        getUserSubscriptions(params)
            .then((response) => {
                setUserSubscriptions(response.data.data);
                setUserTotalRows(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch user subscriptions', error);
            });
    }, [userPaginationModel]);

    useEffect(() => {
        const params = {
            ...athleteChangeRequestSearchParamsRef.current,
            per_page: athleteChangeRequestPaginationModel.pageSize,
            page: athleteChangeRequestPaginationModel.page + 1,
        };
        getAthleteChangeRequests(params as GetAthleteChangeRequestParams)
            .then((response) => {
                setAthleteChangeRequests(response.data.data);
                setAthleteChangeRequestTotalRows(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch athlete change requests', error);
            });
    }, [athleteChangeRequestPaginationModel]);

    const [editedRows, setEditedRows] = useState<{ [key: string]: any }>({});

    const handleFieldChange = (id: string, field: string, value: any) => {
        const originalRow = changeRequests.find((cr) => cr.uuid === id);
        const originalValue =
            originalRow && field in originalRow
                ? originalRow[field as keyof typeof originalRow]
                : undefined;

        setEditedRows((prev) => {
            const newRow = {
                ...prev[id],
                [field]: value === originalValue ? undefined : value,
            };

            if (!Object.values(newRow).some((v) => v !== undefined)) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [id]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [id]: newRow,
            };
        });
    };

    const handleUpdate = async (id: string) => {
        const editedRow = editedRows[id];
        const originalRow = changeRequests.find((cr) => cr.uuid === id);

        if (!editedRow && !originalRow) {
            setSnackMessage('No changes to update');
            return;
        }

        try {
            const updateData = {
                status: editedRow?.status || originalRow?.status,
                notes: editedRow?.notes !== undefined ? editedRow.notes : originalRow?.notes,
            };

            const res = await updateSubscriptionItemChangeRequest(id, updateData);

            if (res.status === 200) {
                setSnackMessage('Change request updated successfully');
                // Update the local state to reflect the changes
                setChangeRequests((prev) =>
                    prev.map((request) =>
                        request.uuid === id ? { ...request, ...updateData } : request,
                    ),
                );
            } else {
                setSnackMessage('Failed to update change request');
            }
        } catch (error) {
            console.error('Error updating change request:', error);
            setSnackMessage('Error updating change request');
        }

        // Clear the edited row from the state
        setEditedRows((prev) => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    };

    interface NotesProps {
        id: string;
        value: string;
        editedRows: { [key: string]: { notes?: string; [key: string]: any } };
        setEditedRows: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
    }

    const NotesCell = React.memo(({ id, value, editedRows, setEditedRows }: NotesProps) => {
        const [localValue, setLocalValue] = useState(editedRows[id]?.notes ?? value);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            setLocalValue(e.target.value);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            e.stopPropagation();
            setEditedRows((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    notes: localValue === value ? undefined : localValue,
                },
            }));
        };

        return (
            <TextField
                value={localValue}
                onChange={handleChange}
                onKeyDown={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onBlur={handleBlur}
                size="small"
                fullWidth
                sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': {
                        height: '40px',
                    },
                }}
            />
        );
    });

    NotesCell.displayName = 'NotesCell';

    const changeRequestColumns: GridColDef[] = [
        {
            field: 'organization_name',
            headerName: 'Organization',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                return params.row.organization?.name || 'N/A';
            },
        },
        {
            field: 'subscription_name',
            headerName: 'Subscription',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                return params.row.subscription?.name || 'N/A';
            },
        },
        {
            field: 'subscription_item_name',
            headerName: 'Subscription Item',
            width: 200,
            sortable: false,
            renderCell: (params: any) => {
                const stripeId = params.row.subscription?.stripe_id;
                const itemName = params.row.subscription_item?.name;

                if (!stripeId || !itemName) {
                    return <span>{itemName || 'N/A'}</span>;
                }

                return (
                    <a
                        href={`https://dashboard.stripe.com/subscriptions/${params.row.subscription.stripe_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {params.row.subscription_item.name}
                    </a>
                );
            },
        },
        { field: 'change_request_type', headerName: 'Change Request', width: 150, sortable: true },
        {
            field: 'tracking_number',
            headerName: 'Tracking #',
            width: 180,
            sortable: false,
            renderCell: (params) =>
                renderTrackingNumberLink(params.row.tracking_number, params.row.shipping_carrier),
        },
        {
            field: 'shipping_carrier',
            headerName: 'Carrier',
            width: 120,
            sortable: false,
            renderCell: (params) => params.row.shipping_carrier || 'N/A',
        },
        {
            field: 'change_request_reason',
            headerName: 'Reason',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {params.row.change_request_reason}
                            </span>
                        }
                        arrow
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'viewport',
                                    },
                                },
                            ],
                        }}
                        sx={{ maxWidth: 300 }}
                    >
                        <span
                            style={{
                                fontSize: '0.9rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'inline-block',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.change_request_reason}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Select
                    variant="outlined"
                    value={editedRows[params.id]?.status || params.value}
                    onChange={(e) =>
                        handleFieldChange(params.id as string, 'status', e.target.value)
                    }
                    size="small"
                    fullWidth
                >
                    <MenuItem value="requested">Requested</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
            ),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 300,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <NotesCell
                    id={params.id as string}
                    value={params.value}
                    editedRows={editedRows}
                    setEditedRows={setEditedRows}
                />
            ),
        },
        {
            field: 'contact',
            headerName: 'Contact',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const contact = params.row.organization?.creator;

                if (!contact) {
                    return <span>N/A</span>;
                }

                const { first_name, last_name, email, phone } = contact;
                const fullName = `${first_name} ${last_name}`;
                const contactInfo = `Email: ${email}\nPhone: ${phone || 'N/A'}`;

                const handleCopy = () => {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard
                            .writeText(contactInfo)
                            .then(() => {
                                console.log('Contact info copied to clipboard');
                            })
                            .catch((err) => {
                                console.error('Failed to copy text: ', err);
                            });
                    } else {
                        const textArea = document.createElement('textarea');
                        textArea.value = contactInfo;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        console.log('Contact info copied to clipboard (fallback method)');
                    }
                };

                return (
                    <Tooltip
                        style={{ cursor: 'pointer' }}
                        title={
                            <React.Fragment>
                                {contactInfo}
                                <IconButton onClick={handleCopy} size="small" sx={{ ml: 1 }}>
                                    <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                            </React.Fragment>
                        }
                        arrow
                    >
                        <span>{fullName}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 200,
            sortable: true,
            valueFormatter: (params: any) => {
                const date = new Date(params * 1000);
                const month = date.getMonth() + 1;
                const day = date.getDate();
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleUpdate(params.id as string)}
                    disabled={!editedRows[params.id]}
                >
                    Update
                </Button>
            ),
        },
    ];

    // Athlete change request handlers
    const handleAthleteFieldChange = (id: string, field: string, value: any) => {
        const originalRow = athleteChangeRequests.find((cr) => cr.uuid === id);
        const originalValue =
            originalRow && field in originalRow
                ? originalRow[field as keyof typeof originalRow]
                : undefined;

        setAthleteEditedRows((prev) => {
            const newRow = {
                ...prev[id],
                [field]: value === originalValue ? undefined : value,
            };

            if (!Object.values(newRow).some((v) => v !== undefined)) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [id]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [id]: newRow,
            };
        });
    };

    const handleAthleteUpdate = async (id: string) => {
        const editedRow = athleteEditedRows[id];
        const originalRow = athleteChangeRequests.find((cr) => cr.uuid === id);

        if (!editedRow && !originalRow) {
            setSnackMessage('No changes to update');
            return;
        }

        try {
            const updateData = {
                status: editedRow?.status || originalRow?.status,
                notes: editedRow?.notes !== undefined ? editedRow.notes : originalRow?.notes,
            };

            const res = await updateAthleteChangeRequest(id, updateData);

            if (res.status === 200) {
                setSnackMessage('Athlete change request updated successfully');
                setAthleteChangeRequests((prev) =>
                    prev.map((request) =>
                        request.uuid === id ? { ...request, ...updateData } : request,
                    ),
                );
            } else {
                setSnackMessage('Failed to update athlete change request');
            }
        } catch (error) {
            console.error('Error updating athlete change request:', error);
            setSnackMessage('Error updating athlete change request');
        }

        setAthleteEditedRows((prev) => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    };

    const AthleteNotesCell = React.memo(
        ({
            id,
            value,
            editedRows: rows,
            setEditedRows: setRows,
        }: {
            id: string;
            value: string;
            editedRows: { [key: string]: any };
            setEditedRows: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
        }) => {
            const [localValue, setLocalValue] = useState(rows[id]?.notes ?? value);

            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                setLocalValue(e.target.value);
            };

            const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                e.stopPropagation();
                setRows((prev) => ({
                    ...prev,
                    [id]: {
                        ...prev[id],
                        notes: localValue === value ? undefined : localValue,
                    },
                }));
            };

            return (
                <TextField
                    value={localValue}
                    onChange={handleChange}
                    onKeyDown={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleBlur}
                    size="small"
                    fullWidth
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiInputBase-root': {
                            height: '40px',
                        },
                    }}
                />
            );
        },
    );
    AthleteNotesCell.displayName = 'AthleteNotesCell';

    const athleteChangeRequestColumns: GridColDef[] = [
        {
            field: 'athlete_name',
            headerName: 'Athlete',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const user = params.row.user;
                return user ? `${user.first_name} ${user.last_name}` : 'N/A';
            },
        },
        { field: 'change_request_type', headerName: 'Change Request', width: 150, sortable: true },
        {
            field: 'pod_serial_number',
            headerName: 'Pod Serial',
            width: 150,
            sortable: false,
            renderCell: (params) => params.row.pod_serial_number || 'N/A',
        },
        {
            field: 'tracking_number',
            headerName: 'Tracking #',
            width: 180,
            sortable: false,
            renderCell: (params) =>
                renderTrackingNumberLink(params.row.tracking_number, params.row.shipping_carrier),
        },
        {
            field: 'shipping_carrier',
            headerName: 'Carrier',
            width: 120,
            sortable: false,
            renderCell: (params) => params.row.shipping_carrier || 'N/A',
        },
        {
            field: 'change_request_reason',
            headerName: 'Reason',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {params.row.change_request_reason}
                            </span>
                        }
                        arrow
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'viewport',
                                    },
                                },
                            ],
                        }}
                        sx={{ maxWidth: 300 }}
                    >
                        <span
                            style={{
                                fontSize: '0.9rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'inline-block',
                                cursor: 'pointer',
                            }}
                        >
                            {params.row.change_request_reason}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Select
                    variant="outlined"
                    value={athleteEditedRows[params.id]?.status || params.value}
                    onChange={(e) =>
                        handleAthleteFieldChange(params.id as string, 'status', e.target.value)
                    }
                    size="small"
                    fullWidth
                >
                    <MenuItem value="requested">Requested</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
            ),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 300,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <AthleteNotesCell
                    id={params.id as string}
                    value={params.value}
                    editedRows={athleteEditedRows}
                    setEditedRows={setAthleteEditedRows}
                />
            ),
        },
        {
            field: 'contact',
            headerName: 'Contact',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const user = params.row.user;
                if (!user) return <span>N/A</span>;

                const fullName = `${user.first_name} ${user.last_name}`;
                const contactInfo = `Email: ${user.email}\nPhone: ${user.phone || 'N/A'}`;

                const handleCopy = () => {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(contactInfo).catch(console.error);
                    } else {
                        const textArea = document.createElement('textarea');
                        textArea.value = contactInfo;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                    }
                };

                return (
                    <Tooltip
                        style={{ cursor: 'pointer' }}
                        title={
                            <React.Fragment>
                                {contactInfo}
                                <IconButton onClick={handleCopy} size="small" sx={{ ml: 1 }}>
                                    <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                            </React.Fragment>
                        }
                        arrow
                    >
                        <span>{fullName}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 200,
            sortable: true,
            valueFormatter: (params: any) => {
                const date = new Date(params * 1000);
                const month = date.getMonth() + 1;
                const day = date.getDate();
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleAthleteUpdate(params.id as string)}
                    disabled={!athleteEditedRows[params.id]}
                >
                    Update
                </Button>
            ),
        },
    ];

    const handleAthleteChangeRequestSearchParameterChange = (event: SelectChangeEvent<string>) => {
        const { name, value } = event.target;
        setAthleteChangeRequestSearchParams((prevParams) => ({
            ...prevParams,
            [name]: value,
        }));
    };

    const handleAthleteChangeRequestSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const params = {
                ...athleteChangeRequestSearchParams,
                status:
                    athleteChangeRequestSearchParams.status === 'All'
                        ? undefined
                        : athleteChangeRequestSearchParams.status,
                page: athleteChangeRequestPaginationModel.page + 1,
                per_page: athleteChangeRequestPaginationModel.pageSize,
            };
            await getAthleteChangeRequestSearchResults(params as GetAthleteChangeRequestParams);
        } catch (error) {
            console.error(error);
        }
    };

    const getAthleteChangeRequestSearchResults = async (params: GetAthleteChangeRequestParams) => {
        const response = await getAthleteChangeRequests(params);
        if (Array.isArray(response.data.data)) {
            const updatedData = response.data.data.map((item) => ({
                ...item,
                notes:
                    athleteEditedRows[item.uuid]?.notes !== undefined
                        ? athleteEditedRows[item.uuid].notes
                        : item.notes,
            }));
            setAthleteChangeRequests(updatedData);
            setAthleteChangeRequestTotalRows(response.data.meta.total);
        } else {
            console.error('Unexpected data format: athlete change requests data is not an array');
        }
    };

    const handleAthleteChangeRequestSort = async (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        const fieldMapping: { [key: string]: string } = {
            change_request_type: 'change_request_type',
            created_at: 'created_at',
        };

        if (sortField) {
            sortField = fieldMapping[sortField] || sortField;
            if (sortOrder === 'desc') {
                sortField = `-${sortField}`;
            }
        }

        const params = {
            ...athleteChangeRequestSearchParams,
            sort: sortField,
            page: athleteChangeRequestPaginationModel.page + 1,
            per_page: athleteChangeRequestPaginationModel.pageSize,
        };

        await getAthleteChangeRequestSearchResults(params as GetAthleteChangeRequestParams);
    };

    const handleChangeRequestSearchParameterChange = (
        event:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | SelectChangeEvent<string>,
    ) => {
        const { name, value } = event.target;
        setChangeRequestSearchParams((prevParams) => ({
            ...prevParams,
            [name]: value,
        }));
    };

    const handleChangeRequestSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const params = {
                ...changeRequestSearchParams,
                organization_uuid: changeRequestSearchParams.organization_uuid || undefined,
                status:
                    changeRequestSearchParams.status === 'All'
                        ? undefined
                        : changeRequestSearchParams.status,
                page: changeRequestPaginationModel.page + 1,
                per_page: changeRequestPaginationModel.pageSize,
            };
            await getChangeRequestSearchResults(params as GetSubscriptionItemChangeRequestParams);
        } catch (error) {
            console.error(error);
        }
    };

    const getChangeRequestSearchResults = async (
        params: GetSubscriptionItemChangeRequestParams,
    ) => {
        const response = await getSubscriptionItemChangeRequests(params);
        if (Array.isArray(response.data.data)) {
            const updatedData = response.data.data.map((item) => ({
                ...item,
                notes:
                    editedRows[item.uuid]?.notes !== undefined
                        ? editedRows[item.uuid].notes
                        : item.notes,
            }));
            setChangeRequests(updatedData);
            setChangeRequestTotalRows(response.data.meta.total);
        } else {
            console.error('Unexpected data format: change requests data is not an array');
        }
    };

    const handleChangeRequestSort = async (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        const fieldMapping: { [key: string]: string } = {
            organization_name: 'organization.name',
            subscription_name: 'subscription.name',
            subscription_item_name: 'subscription_item.name',
            change_request_type: 'change_request_type',
            created_at: 'created_at',
        };

        if (sortField) {
            sortField = fieldMapping[sortField] || sortField;

            if (sortOrder === 'desc') {
                sortField = `-${sortField}`;
            }
        }

        const params = {
            ...changeRequestSearchParams,
            sort: sortField,
            page: changeRequestPaginationModel.page + 1,
            per_page: changeRequestPaginationModel.pageSize,
        };

        await getChangeRequestSearchResults(params as GetSubscriptionItemChangeRequestParams);
    };

    const fetchOrganizations = debounce((searchTerm: string, perPage?: number) => {
        adminGetAllOrganizations(searchTerm, perPage)
            .then((response) => {
                setOrganizations(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
            });
    }, 300);

    useEffect(() => {
        fetchOrganizations('', 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const orgColumns: GridColDef[] = [
        { field: 'organization_name', headerName: 'Organization', width: 150, sortable: true },
        {
            field: 'organization.pod_count',
            headerName: 'Org Pods',
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<OrgSubscription, any, any>) => (
                <span>{params.row.organization?.pod_count ?? 0}</span>
            ),
        },
        {
            field: 'organization.transferred_pod_count',
            headerName: 'Transferred Pods',
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<OrgSubscription, any, any>) => (
                <span>{params.row.organization?.transferred_pod_count ?? 0}</span>
            ),
        },
        {
            field: 'name',
            headerName: 'Subscription Name',
            width: 200,
            sortable: true,
            renderCell: (params: GridRenderCellParams<Subscription, string>) => {
                const { items } = params.row as Subscription;
                const itemCount = items?.length ?? 0;

                if (!items || items.length === 0) {
                    return <span style={{ fontSize: '0.8rem' }}>{params.value} [0]</span>;
                }

                return (
                    <Tooltip
                        title={
                            <div>
                                {items.map((item) => (
                                    <div key={item.id}>
                                        {item.name} [{item.quantity}]
                                    </div>
                                ))}
                            </div>
                        }
                        arrow
                    >
                        <span style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                            {params.value} [{itemCount}]
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'stripe_id',
            headerName: 'Stripe Subscription ID',
            width: 250,
            sortable: true,
            renderCell: (params: GridRenderCellParams<OrgSubscription, any, any>) => (
                <a
                    href={`https://dashboard.stripe.com/subscriptions/${params.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {params.value}
                </a>
            ),
        },
        {
            field: 'stripe_price',
            headerName: 'Stripe Price ID',
            width: 250,
            sortable: true,
            renderCell: (params: GridRenderCellParams<OrgSubscription, any, any>) => (
                <a
                    href={`https://dashboard.stripe.com/prices/${params.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {params.value}
                </a>
            ),
        },
        {
            field: 'stripe_status',
            headerName: 'Status',
            width: 150,
            sortable: true,
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            width: 100,
            sortable: true,
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            width: 200,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'trial_ends_at',
            headerName: 'Trial End Date',
            width: 150,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'ends_at',
            headerName: 'End Date',
            width: 150,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
    ];
    const userColumns: GridColDef[] = [
        { field: 'user_name', headerName: 'User', width: 150, sortable: true },
        { field: 'name', headerName: 'Subscription Name', width: 200, sortable: true },
        {
            field: 'stripe_id',
            headerName: 'Stripe Subscription ID',
            width: 250,
            sortable: true,
            renderCell: (params: GridRenderCellParams<UserSubscription, any, any>) => (
                <a
                    href={`https://dashboard.stripe.com/subscriptions/${params.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {params.value}
                </a>
            ),
        },
        {
            field: 'stripe_price',
            headerName: 'Stripe Price ID',
            width: 250,
            sortable: true,
            renderCell: (params: GridRenderCellParams<UserSubscription, any, any>) => (
                <a
                    href={`https://dashboard.stripe.com/prices/${params.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {params.value}
                </a>
            ),
        },
        {
            field: 'stripe_status',
            headerName: 'Status',
            width: 150,
            sortable: true,
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            width: 100,
            sortable: true,
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            width: 200,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'trial_ends_at',
            headerName: 'Trial End Date',
            width: 150,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'ends_at',
            headerName: 'End Date',
            width: 150,
            sortable: true,
            valueFormatter: (value: string | number | Date | null) =>
                value ? new Date(value).toLocaleDateString() : '',
        },
    ];
    const billingPortalConfigColumns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'Configuration ID',
            width: 250,
            renderCell: (params: GridRenderCellParams) => (
                <a
                    href="https://dashboard.stripe.com/settings/billing/portal"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {params.value}
                </a>
            ),
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 150,
            renderCell: (params) => params.value || 'Default',
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 80,
            renderCell: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
            field: 'is_default',
            headerName: 'Default',
            width: 80,
            renderCell: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
            field: 'business_profile_headline',
            headerName: 'Business Headline',
            width: 200,
        },
        {
            field: 'invoice_history_enabled',
            headerName: 'Invoice History',
            width: 130,
            renderCell: (params) => (params.value ? 'Enabled' : 'Disabled'),
        },
        {
            field: 'customer_update_enabled',
            headerName: 'Customer Update',
            width: 140,
            renderCell: (params) => (params.value ? 'Enabled' : 'Disabled'),
        },
        {
            field: 'payment_method_update_enabled',
            headerName: 'Payment Method Update',
            width: 180,
            renderCell: (params) => (params.value ? 'Enabled' : 'Disabled'),
        },
        {
            field: 'subscription_cancel_enabled',
            headerName: 'Subscription Cancel',
            width: 160,
            renderCell: (params) => {
                const mode = params.row.subscription_cancel_mode;
                return params.value ? `Enabled (${mode})` : 'Disabled';
            },
        },
        {
            field: 'subscription_pause_enabled',
            headerName: 'Subscription Pause',
            width: 150,
            renderCell: (params) => (params.value ? 'Enabled' : 'Disabled'),
        },
        {
            field: 'subscription_update_enabled',
            headerName: 'Subscription Update',
            width: 160,
            renderCell: (params) => (params.value ? 'Enabled' : 'Disabled'),
        },
        {
            field: 'created',
            headerName: 'Created',
            width: 150,
            valueFormatter: (value: number) => {
                const date = new Date(value * 1000);
                return date.toLocaleDateString();
            },
        },
        {
            field: 'updated',
            headerName: 'Updated',
            width: 150,
            valueFormatter: (value: number) => {
                const date = new Date(value * 1000);
                return date.toLocaleDateString();
            },
        },
    ];
    const handleOrgSearchParameterChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setOrgSearchParams({
            ...orgSearchParams,
            [event.target.name as string]: event.target.value,
        });
    };

    const handleUserSearchParameterChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setUserSearchParams({
            ...userSearchParams,
            [event.target.name as string]: event.target.value,
        });
    };

    const handleOrgSubscriptionSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const params = {
                ...orgSearchParams,
                organization_uuid: orgSearchParams.organization_uuid || undefined,
                page: orgPaginationModel.page + 1,
                per_page: orgPaginationModel.pageSize,
            };
            await getOrgSubscriptionSearchResults(params);
        } catch (error) {
            console.error(error);
        }
    };

    const handleUserSubscriptionSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const params = {
                ...userSearchParams,
                user_name: userSearchParams.user_name || undefined,
                page: userPaginationModel.page + 1,
                per_page: userPaginationModel.pageSize,
            };
            await getUserSubscriptionSearchResults(params);
        } catch (error) {
            console.error(error);
        }
    };

    const getOrgSubscriptionSearchResults = async (params: any) => {
        const paginatedParams = {
            ...params,
            page: orgPaginationModel.page + 1,
            per_page: orgPaginationModel.pageSize,
        };
        const response = await getOrgSubscriptions(paginatedParams);
        if (Array.isArray(response.data.data)) {
            setOrgSubscriptions(response.data.data);
            setOrgTotalRows(response.data.meta.total);
        } else {
            console.error('Unexpected data format: subscriptions data is not an array');
        }
    };

    const getUserSubscriptionSearchResults = async (params: any) => {
        const paginatedParams = {
            ...params,
            page: userPaginationModel.page + 1,
            per_page: userPaginationModel.pageSize,
        };
        const response = await getUserSubscriptions(paginatedParams);
        if (Array.isArray(response.data.data)) {
            setUserSubscriptions(response.data.data);
            setUserTotalRows(response.data.meta.total);
        } else {
            console.error('Unexpected data format: user subscriptions data is not an array');
        }
    };

    const handleOrgSubscriptionSort = async (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        if (sortField && sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }

        const params = {
            ...orgSearchParams,
            sort: sortField as GetOrgSubscriptionParams['sort'],
            page: orgPaginationModel.page + 1,
            per_page: orgPaginationModel.pageSize,
        };

        await getOrgSubscriptionSearchResults(params);
    };

    const handleUserSubscriptionSort = async (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        if (sortField && sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }

        const params = {
            ...userSearchParams,
            sort: sortField as GetUserSubscriptionParams['sort'],
            page: userPaginationModel.page + 1,
            per_page: userPaginationModel.pageSize,
        };

        await getUserSubscriptionSearchResults(params);
    };

    const handleOrgSelectStatusChange = (event: SelectChangeEvent<string>) => {
        setOrgSearchParams({
            ...orgSearchParams,
            stripe_status: event.target.value as GetOrgSubscriptionParams['stripe_status'],
        });
    };

    const handleUserSelectStatusChange = (event: SelectChangeEvent<string>) => {
        setUserSearchParams({
            ...userSearchParams,
            stripe_status: event.target.value as GetUserSubscriptionParams['stripe_status'],
        });
    };

    const handleGridSelectionChange = (event: SelectChangeEvent<SelectedGrid>) => {
        setSelectedGrid(event.target.value as SelectedGrid);
    };

    return (
        <Box sx={{ margin: theme.spacing(0, 2) }}>
            <Snackbar
                open={!!snackMessage}
                autoHideDuration={6000}
                message={snackMessage}
                onClose={() => setSnackMessage('')}
            />
            <h2>Subscriptions</h2>
            <p
                style={{
                    fontSize: '0.9rem',
                    color: theme.palette.text.secondary,
                    marginTop: '-8px',
                }}
            >
                View and manage subscription item change requests, organization subscriptions, user
                subscriptions, and billing portal configurations
            </p>

            <Box sx={{ marginBottom: 3, maxWidth: '400px' }}>
                <FormControl fullWidth>
                    <InputLabel id="grid-selection-label">View</InputLabel>
                    <Select
                        labelId="grid-selection-label"
                        value={selectedGrid}
                        onChange={handleGridSelectionChange}
                        label="View"
                        size="small"
                    >
                        <MenuItem value="subscription_item_change_requests">
                            Subscription Item Change Requests
                        </MenuItem>
                        <MenuItem value="athlete_change_requests">Athlete Change Requests</MenuItem>
                        <MenuItem value="organization_subscriptions">
                            Organization Subscriptions
                        </MenuItem>
                        <MenuItem value="user_subscriptions">User Subscriptions</MenuItem>
                        <MenuItem value="billing_portal_configurations">
                            Billing Portal Configurations
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {selectedGrid === 'subscription_item_change_requests' && (
                <>
                    <section style={{ marginBottom: '2rem' }}>
                        <h4>Subscription Item Change Requests</h4>
                        <ResponsiveForm onSubmit={handleChangeRequestSearch} noValidate>
                            <ResponsiveFormItem>
                                <Autocomplete
                                    value={
                                        organizations.find(
                                            (org) =>
                                                org.uuid ===
                                                changeRequestSearchParams.organization_uuid,
                                        ) || null
                                    }
                                    onChange={(event, newValue) => {
                                        setChangeRequestSearchParams({
                                            ...changeRequestSearchParams,
                                            organization_uuid: newValue ? newValue.uuid : '',
                                        });
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason !== 'reset') {
                                            fetchOrganizations(newInputValue);
                                        }
                                    }}
                                    getOptionKey={(option) => option.uuid}
                                    filterOptions={(options) => options}
                                    size={'small'}
                                    fullWidth
                                    options={organizations}
                                    getOptionLabel={(option) => `${option.name} - ${option.uuid}`}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Organizations" />
                                    )}
                                />
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="change-request-status-label">Status</InputLabel>
                                    <Select
                                        labelId="change-request-status-label"
                                        id="change-request-status"
                                        name="status"
                                        value={changeRequestSearchParams.status}
                                        onChange={handleChangeRequestSearchParameterChange}
                                        label="Status"
                                        variant="outlined"
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        <MenuItem value="requested">Requested</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                    </Select>
                                </FormControl>
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <Button
                                    type="submit"
                                    size={'medium'}
                                    variant="contained"
                                    fullWidth={isNarrow}
                                >
                                    Search Change Requests
                                </Button>
                            </ResponsiveFormItem>
                        </ResponsiveForm>
                    </section>

                    <Box
                        width="98%"
                        maxHeight="600px"
                        overflow="auto"
                        className="dataGridContainer"
                    >
                        <DataGridPremium
                            rows={changeRequests}
                            columns={changeRequestColumns}
                            checkboxSelection={false}
                            sortingMode="server"
                            onSortModelChange={(model) => handleChangeRequestSort(model)}
                            getRowId={(row) => row.uuid}
                            style={{ height: '624px', marginBottom: '50px' }}
                            paginationMode="server"
                            disableColumnMenu={true}
                            pagination
                            rowCount={changeRequestTotalRows}
                            paginationModel={changeRequestPaginationModel}
                            onPaginationModelChange={setChangeRequestPaginationModel}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            sx={dataGridSx}
                        />
                    </Box>
                </>
            )}

            {selectedGrid === 'athlete_change_requests' && (
                <>
                    <section style={{ marginBottom: '2rem' }}>
                        <h4>Athlete Change Requests</h4>
                        <ResponsiveForm onSubmit={handleAthleteChangeRequestSearch} noValidate>
                            <ResponsiveFormItem>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="athlete-change-request-status-label">
                                        Status
                                    </InputLabel>
                                    <Select
                                        labelId="athlete-change-request-status-label"
                                        id="athlete-change-request-status"
                                        name="status"
                                        value={athleteChangeRequestSearchParams.status}
                                        onChange={handleAthleteChangeRequestSearchParameterChange}
                                        label="Status"
                                        variant="outlined"
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        <MenuItem value="requested">Requested</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                    </Select>
                                </FormControl>
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <Button
                                    type="submit"
                                    size={'medium'}
                                    variant="contained"
                                    fullWidth={isNarrow}
                                >
                                    Search Athlete Requests
                                </Button>
                            </ResponsiveFormItem>
                        </ResponsiveForm>
                    </section>

                    <Box
                        width="98%"
                        maxHeight="600px"
                        overflow="auto"
                        className="dataGridContainer"
                    >
                        <DataGridPremium
                            rows={athleteChangeRequests}
                            columns={athleteChangeRequestColumns}
                            checkboxSelection={false}
                            sortingMode="server"
                            onSortModelChange={(model) => handleAthleteChangeRequestSort(model)}
                            getRowId={(row) => row.uuid}
                            style={{ height: '624px', marginBottom: '50px' }}
                            paginationMode="server"
                            disableColumnMenu={true}
                            pagination
                            rowCount={athleteChangeRequestTotalRows}
                            paginationModel={athleteChangeRequestPaginationModel}
                            onPaginationModelChange={setAthleteChangeRequestPaginationModel}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            sx={dataGridSx}
                        />
                    </Box>
                </>
            )}

            {selectedGrid === 'organization_subscriptions' && (
                <>
                    <section style={{ marginBottom: '2rem' }}>
                        <h4>Filter Organization Subscriptions</h4>
                        <ResponsiveForm onSubmit={handleOrgSubscriptionSearch} noValidate>
                            <ResponsiveFormItem>
                                <Autocomplete
                                    value={
                                        organizations.find(
                                            (org) => org.uuid === orgSearchParams.organization_uuid,
                                        ) || null
                                    }
                                    onChange={(event, newValue) => {
                                        setOrgSearchParams({
                                            ...orgSearchParams,
                                            organization_uuid: newValue ? newValue.uuid : '',
                                        });
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason !== 'reset') {
                                            fetchOrganizations(newInputValue);
                                        }
                                    }}
                                    getOptionKey={(option) => option.uuid}
                                    filterOptions={(options) => options}
                                    size={'small'}
                                    fullWidth
                                    options={organizations}
                                    getOptionLabel={(option) => `${option.name} - ${option.uuid}`}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Organizations" />
                                    )}
                                />
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <TextField
                                    name="name"
                                    label="Subscription Name"
                                    value={orgSearchParams.name}
                                    onChange={handleOrgSearchParameterChange}
                                    placeholder="Enter Subscription Name"
                                    size={'small'}
                                    fullWidth
                                />
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <FormControl fullWidth>
                                    <InputLabel id="org-status-label">Status</InputLabel>
                                    <Select
                                        labelId="org-status-label"
                                        size="small"
                                        value={orgSearchParams.stripe_status}
                                        onChange={handleOrgSelectStatusChange}
                                        label="Status"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="past_due">Past Due</MenuItem>
                                        <MenuItem value="canceled">Canceled</MenuItem>
                                    </Select>
                                </FormControl>
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <Button
                                    type="submit"
                                    size={'medium'}
                                    variant="contained"
                                    fullWidth={isNarrow}
                                >
                                    Search Subscriptions
                                </Button>
                            </ResponsiveFormItem>
                        </ResponsiveForm>
                    </section>

                    <Box
                        width="98%"
                        maxHeight="600px"
                        overflow="auto"
                        className="dataGridContainer"
                    >
                        <DataGridPremium
                            rows={orgSubscriptions}
                            columns={orgColumns}
                            checkboxSelection={false}
                            sortingMode="server"
                            onSortModelChange={(model) => handleOrgSubscriptionSort(model)}
                            getRowId={(row) => row.id}
                            style={{ height: '624px', marginBottom: '50px' }}
                            paginationMode="server"
                            disableColumnMenu={true}
                            pagination
                            rowCount={orgTotalRows}
                            paginationModel={orgPaginationModel}
                            onPaginationModelChange={setOrgPaginationModel}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            sx={dataGridSx}
                        />
                    </Box>
                </>
            )}

            {selectedGrid === 'user_subscriptions' && (
                <>
                    <section style={{ marginBottom: '2rem' }}>
                        <h4>Filter User Subscriptions</h4>
                        <ResponsiveForm onSubmit={handleUserSubscriptionSearch} noValidate>
                            <ResponsiveFormItem>
                                <TextField
                                    label="User Name"
                                    name="user_name"
                                    value={userSearchParams.user_name}
                                    onChange={handleUserSearchParameterChange}
                                    placeholder="Enter User Name"
                                    size={'small'}
                                    fullWidth
                                />
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <TextField
                                    label="Subscription Name"
                                    name="name"
                                    value={userSearchParams.name}
                                    onChange={handleUserSearchParameterChange}
                                    placeholder="Enter Subscription Name"
                                    size={'small'}
                                    fullWidth
                                />
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <FormControl fullWidth>
                                    <InputLabel id="user-status-label">Status</InputLabel>
                                    <Select
                                        variant="outlined"
                                        labelId="user-status-label"
                                        size="small"
                                        value={userSearchParams.stripe_status}
                                        onChange={handleUserSelectStatusChange}
                                        label="Status"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="past_due">Past Due</MenuItem>
                                        <MenuItem value="canceled">Canceled</MenuItem>
                                    </Select>
                                </FormControl>
                            </ResponsiveFormItem>
                            <ResponsiveFormItem>
                                <Button
                                    type="submit"
                                    size={'medium'}
                                    variant="contained"
                                    fullWidth={isNarrow}
                                >
                                    Search Subscriptions
                                </Button>
                            </ResponsiveFormItem>
                        </ResponsiveForm>
                    </section>

                    <Box
                        width="98%"
                        maxHeight="600px"
                        overflow="auto"
                        className="dataGridContainer"
                    >
                        <DataGridPremium
                            rows={userSubscriptions}
                            columns={userColumns}
                            checkboxSelection={false}
                            sortingMode="server"
                            onSortModelChange={(model) => handleUserSubscriptionSort(model)}
                            getRowId={(row) => row.id}
                            style={{ marginBottom: '50px' }}
                            paginationMode="server"
                            pagination
                            rowCount={userTotalRows}
                            paginationModel={userPaginationModel}
                            disableColumnMenu={true}
                            onPaginationModelChange={setUserPaginationModel}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            sx={dataGridSx}
                        />
                    </Box>
                </>
            )}

            {selectedGrid === 'billing_portal_configurations' && (
                <>
                    <section style={{ marginBottom: '2rem' }}>
                        <h4>Billing Portal Configurations</h4>
                    </section>

                    <Box
                        width="98%"
                        maxHeight="600px"
                        overflow="auto"
                        className="dataGridContainer"
                    >
                        <DataGridPremium
                            rows={billingPortalConfigs}
                            columns={billingPortalConfigColumns}
                            checkboxSelection={false}
                            getRowId={(row) => row.id}
                            style={{ marginBottom: '50px' }}
                            disableColumnMenu={true}
                            hideFooter
                            sx={dataGridSx}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default SubscriptionList;
